import React, { createContext, useContext, useRef, ReactNode } from 'react';
import { debugLog } from '@/utils/debugLogger';
import { ttsService } from '@/services/textToSpeech/ttsService';

interface AudioPlayer {
  id: string;
  audio: HTMLAudioElement | null;
  stop: () => void;
  audioUrl?: string; // Track audio URL for cleanup
}

interface AudioContextType {
  registerAudioPlayer: (id: string, audio: HTMLAudioElement | null, stopCallback: () => void, audioUrl?: string) => void;
  unregisterAudioPlayer: (id: string) => void;
  stopAllAudio: () => void;
  isAudioPlaying: () => boolean;
  getActiveAudioIds: () => string[];
  isAudioProtectionMode: () => boolean;
  setAudioProtectionMode: (enabled: boolean) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

interface AudioProviderProps {
  children: ReactNode;
}

export function AudioProvider({ children }: AudioProviderProps) {
  const audioPlayersRef = useRef<Map<string, AudioPlayer>>(new Map());
  const audioProtectionModeRef = useRef<boolean>(false);
  const pendingInvalidationsRef = useRef<(() => void)[]>([]);

  const registerAudioPlayer = (id: string, audio: HTMLAudioElement | null, stopCallback: () => void, audioUrl?: string) => {
    const player: AudioPlayer = {
      id,
      audio,
      stop: stopCallback,
      audioUrl,
    };
    
    audioPlayersRef.current.set(id, player);
    
    // Enable audio protection when audio is registered and playing
    if (audio && !audio.paused) {
      audioProtectionModeRef.current = true;
      debugLog('audio', `Audio protection ENABLED for player: ${id}`, undefined, 'AudioProvider');
    }
    
    debugLog('audio', `Registered audio player: ${id} ${audio ? 'with audio element' : 'without audio element'}${audioUrl ? ' with URL' : ''}`, undefined, 'AudioProvider');
  };

  const unregisterAudioPlayer = (id: string) => {
    const player = audioPlayersRef.current.get(id);
    if (player?.audioUrl) {
      // Cleanup audio URL to prevent memory leaks
      try {
        ttsService.cleanupAudioUrl(player.audioUrl);
        debugLog('audio', `Cleaned up audio URL for player: ${id}`, undefined, 'AudioProvider');
      } catch (error) {
        console.warn(`[AudioProvider] Failed to cleanup audio URL for ${id}:`, error);
      }
    }
    audioPlayersRef.current.delete(id);
    
    // Check if no more audio is playing and disable protection
    if (!isAudioPlaying()) {
      audioProtectionModeRef.current = false;
      debugLog('audio', `Audio protection DISABLED - no active players`, undefined, 'AudioProvider');
      
      // Execute any pending invalidations
      if (pendingInvalidationsRef.current.length > 0) {
        debugLog('audio', `Executing ${pendingInvalidationsRef.current.length} pending invalidations`, undefined, 'AudioProvider');
        pendingInvalidationsRef.current.forEach(fn => fn());
        pendingInvalidationsRef.current = [];
      }
    }
    
    debugLog('audio', `Unregistered audio player: ${id}`, undefined, 'AudioProvider');
  };

  const stopAllAudio = () => {
    const players = audioPlayersRef.current;
    if (players.size > 0) {
      debugLog('audio', `Stopping ${players.size} audio player(s) due to route change`, undefined, 'AudioProvider');
      
      players.forEach((player) => {
        try {
          // Additional safety: if audio element exists, pause and reset it
          if (player.audio) {
            player.audio.pause();
            player.audio.currentTime = 0;
          }
          player.stop();
          
          // Cleanup audio URL to prevent memory leaks
          if (player.audioUrl) {
            try {
              ttsService.cleanupAudioUrl(player.audioUrl);
              debugLog('audio', `Cleaned up audio URL for player: ${player.id}`, undefined, 'AudioProvider');
            } catch (error) {
              console.warn(`[AudioProvider] Failed to cleanup audio URL for ${player.id}:`, error);
            }
          }
        } catch (error) {
          console.warn(`[AudioProvider] Error stopping audio player ${player.id}:`, error);
        }
      });
      
      // Clear all players after stopping and disable protection
      players.clear();
      audioProtectionModeRef.current = false;
      
      // Execute any pending invalidations since all audio is stopped
      if (pendingInvalidationsRef.current.length > 0) {
        debugLog('audio', `Executing ${pendingInvalidationsRef.current.length} pending invalidations after stopAllAudio`, undefined, 'AudioProvider');
        pendingInvalidationsRef.current.forEach(fn => fn());
        pendingInvalidationsRef.current = [];
      }
      
      debugLog('audio', 'All audio players stopped, cleaned up, and cleared', undefined, 'AudioProvider');
    }
  };

  const isAudioPlaying = () => {
    const players = audioPlayersRef.current;
    for (const [, player] of players) {
      if (player.audio && !player.audio.paused) {
        return true;
      }
    }
    return false;
  };

  const getActiveAudioIds = () => {
    const players = audioPlayersRef.current;
    const activeIds: string[] = [];
    for (const [id, player] of players) {
      if (player.audio && !player.audio.paused) {
        activeIds.push(id);
      }
    }
    return activeIds;
  };

  const isAudioProtectionMode = () => {
    return audioProtectionModeRef.current;
  };

  const setAudioProtectionMode = (enabled: boolean) => {
    const wasEnabled = audioProtectionModeRef.current;
    audioProtectionModeRef.current = enabled;
    
    debugLog('audio', `Audio protection mode ${enabled ? 'ENABLED' : 'DISABLED'}`, undefined, 'AudioProvider');
    
    // If protection is being disabled and there are pending invalidations, execute them
    if (wasEnabled && !enabled && pendingInvalidationsRef.current.length > 0) {
      debugLog('audio', `Executing ${pendingInvalidationsRef.current.length} pending invalidations`, undefined, 'AudioProvider');
      pendingInvalidationsRef.current.forEach(fn => fn());
      pendingInvalidationsRef.current = [];
    }
  };

  const value: AudioContextType = {
    registerAudioPlayer,
    unregisterAudioPlayer,
    stopAllAudio,
    isAudioPlaying,
    getActiveAudioIds,
    isAudioProtectionMode,
    setAudioProtectionMode,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
}